import { useCallback } from 'react'

import {
  useAuthenticatedPerson,
  useDecodedAccessToken,
  useLogout,
} from '@b-stock/fe-core/bstock-next/components'
import { clearPersonalizationCache } from '@b-stock/fe-core/bstock-next/hooks'
import { type GlobalHeaderProps } from '@b-stock/fe-core/bstock-react'

import { clearBannerDismissals } from '@/buyer-portal/components/features/listing/SellerCollectsBanner/sellerCollectsBannerDismissal'
import { redirectSignUp, redirectToAuth } from '@/helpers/auth'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

export function useAuthProps() {
  const { trackButtonClicked } = useAnalytics()

  const person = useAuthenticatedPerson()
  const decodedAccessToken = useDecodedAccessToken()
  const logout = useLogout()

  const isAuthenticated = !!decodedAccessToken
  const accountId = decodedAccessToken?.accountId
  const personId = decodedAccessToken?.personId

  const handleLogout = useCallback(() => {
    // Clear personalization cache so a different account gets a fresh eligibility
    // check on the next login, preventing cross-account session contamination.
    clearPersonalizationCache()
    // Same reason: the Seller Collects banner dismissals live in sessionStorage,
    // which outlives a sign-out in the same tab.
    clearBannerDismissals()
    void logout().then(() => {
      // Redirect to sign-in instead of reloading the current page. On migrated
      // /buy/* settings pages a reload re-mounts authenticated queries with a now-
      // cleared token, which throw and surface the 500 error boundary.
      // redirectToAuth navigates to /acct/signin?redirectAfterLogin=<current path>,
      // matching accounts-portal's logout behavior.
      redirectToAuth()
    })
  }, [logout])

  const handleSignIn = useCallback(() => {
    redirectToAuth()
    trackButtonClicked({
      buttonName: 'sign_in',
      url: document.URL,
      referrer: document.referrer,
      cta_url: '/acct/signin',
    })
  }, [trackButtonClicked])

  const handleRegister = useCallback(() => {
    redirectSignUp()
    trackButtonClicked({
      buttonName: 'register_now',
      url: document.URL,
      referrer: document.referrer,
      cta_url: '/acct/signup',
    })
  }, [trackButtonClicked])

  return {
    user: {
      isAuthenticated,
      firstName: person?.firstName,
      lastName: person?.lastName,
      accountId,
      personId,
    },
    onRegister: handleRegister,
    onSignIn: handleSignIn,
    onLogout: handleLogout,
  } satisfies Pick<
    GlobalHeaderProps,
    'user' | 'onSignIn' | 'onRegister' | 'onLogout'
  >
}
